import { parse } from 'cfb';

// Unified file type registry with signatures, MIME types, and extensions
const fileTypeRegistry = [
	// Images
	{ signature: '89504E47', mimeType: 'image/png', extension: '.png' },
	{ signature: 'FFD8FFDB', mimeType: 'image/jpeg', extension: '.jpg' },
	{ signature: 'FFD8FFE0', mimeType: 'image/jpeg', extension: '.jpg' },
	{ signature: 'FFD8FFE1', mimeType: 'image/jpeg', extension: '.jpg' },
	{ signature: 'FFD8FFE2', mimeType: 'image/jpeg', extension: '.jpg' },
	{ signature: 'FFD8FFE3', mimeType: 'image/jpeg', extension: '.jpg' },
	{ signature: 'FFD8FFE8', mimeType: 'image/jpeg', extension: '.jpg' },
	{ signature: '47494638', mimeType: 'image/gif', extension: '.gif' },
	{ signature: '47494637', mimeType: 'image/gif', extension: '.gif' },
	{ signature: '52494646', mimeType: 'image/webp', extension: '.webp' },
	{ signature: '49492A00', mimeType: 'image/tiff', extension: '.tiff' },
	{ signature: '4D4D002A', mimeType: 'image/tiff', extension: '.tiff' },
	
	// Documents
	{ signature: '25504446', mimeType: 'application/pdf', extension: '.pdf' },
	{ signature: '504B0304', mimeType: 'application/zip', extension: '.zip' }, // Generic ZIP signature - will be refined by content analysis
    { signature: 'D0CF11E0', mimeType: 'application/msword', extension: '.doc' },
	
	// Additional MIME types without specific signatures (detected by content analysis)
	{ signature: null, mimeType: 'application/vnd.ms-excel', extension: '.xls' },
	{ signature: null, mimeType: 'application/vnd.ms-powerpoint', extension: '.ppt' },
	{ signature: null, mimeType: 'application/vnd.android.package-archive', extension: '.apk' },
	{ signature: null, mimeType: 'application/vnd.oasis.opendocument.formula', extension: '.odf' },
	{ signature: null, mimeType: 'application/epub+zip', extension: '.epub' },
	{ signature: null, mimeType: 'application/java-archive', extension: '.jar' },
	{ signature: null, mimeType: 'application/vsix', extension: '.vsix' },
	{ signature: null, mimeType: 'application/vnd.ms-appx.package', extension: '.appx' },
	{ signature: null, mimeType: 'application/vnd.google-earth.kmz', extension: '.kmz' },
	{ signature: null, mimeType: 'text/plain', extension: '.txt' },
	{ signature: null, mimeType: 'application/vnd.oasis.opendocument.text', extension: '.odt' }
];

const getFileBytes = async (file, length = 4) => {
	try {
		const buffer = await file.slice(0, length).arrayBuffer();
		const bytes = new Uint8Array(buffer);
		return bytes;
	} catch (error) {
		console.error('Error reading file signature:', error);
		return new Uint8Array();
	}
};

/**
 * Get file signature (magic bytes) from file
 */
const getFileSignature = async (file, length = 4) => {
	try {
		const buffer = await file.slice(0, length).arrayBuffer();
		const bytes = new Uint8Array(buffer);
		const hex = bytes.reduce((acc, byte) => acc + byte.toString(16).padStart(2, '0'), '');
		return hex.toUpperCase();
	} catch (error) {
		console.error('Error reading file signature:', error);
		return '';
	}
};

const isAsciiText = (bytes) => {
    const threshold = 0.95;
    let asciiCount = 0;
    for (const byte of bytes) {
      if (
        byte === 9 || byte === 10 || byte === 13 ||
        (byte >= 32 && byte <= 126)
      ) {
        asciiCount++;
      }
    }
    return (asciiCount / bytes.length) >= threshold;
  };

  /**
 * Detect legacy Microsoft Office formats: .doc, .xls, .ppt
 * Based on the internal stream name found at offset ~512
 */
  const detectLegacyOfficeFormat = async (file) => {
	try {
	  const arrayBuffer = await file.arrayBuffer();
	  const uint8Array = new Uint8Array(arrayBuffer);
  
	  const cfb = parse(uint8Array, { type: 'buffer' });
  
	  const entries = cfb.FullPaths.map(p => p.toLowerCase());
  
	  if (entries.some(e => e.includes('worddocument'))) {
		return 'application/msword';
	  }
  
	  if (entries.some(e => e.includes('powerpoint document'))) {
		return 'application/vnd.ms-powerpoint';
	  }
  
	  if (entries.some(e => e.includes('workbook') || e.includes('book'))) {
		return 'application/vnd.ms-excel';
	  }
  
	  return 'application/x-ole-storage';
	} catch (error) {
	  console.error('CFB legacy format detection failed:', error);
	  return 'application/x-ole-storage';
	}
  };
  

/**
 * Detect specific ZIP-based format by scanning file contents
 */
const detectKnownFormatFromBuffer = async (file) => {
	try {
		const buffer = await file.slice(0, 10000).arrayBuffer();
		const text = new TextDecoder('ascii').decode(buffer);

		if (text.includes('word/document.xml')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
		if (text.includes('xl/workbook.xml')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
		if (text.includes('ppt/presentation.xml')) return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
		if (text.includes('AndroidManifest.xml')) return 'application/vnd.android.package-archive';
		if (text.includes('content.xml') && text.includes('META-INF/manifest.xml') && text.includes('office:mimetype="application/vnd.oasis.opendocument.formula"')) return 'application/vnd.oasis.opendocument.formula';
		if (text.includes('mimetype') && text.includes('application/epub+zip')) return 'application/epub+zip';
		if (text.includes('META-INF/MANIFEST.MF')) return 'application/java-archive';
		if (text.includes('WEB-INF/web.xml')) return 'application/java-archive';
		if (text.includes('extension.vsixmanifest')) return 'application/vsix';
		if (text.includes('AppxManifest.xml')) return 'application/vnd.ms-appx.package';
		if (text.includes('.kml')) return 'application/vnd.google-earth.kmz';
		if (text.includes('mimetype') && text.includes('application/vnd.oasis.opendocument.text')) { return 'application/vnd.oasis.opendocument.text'; }		  

		return 'application/zip';

	} catch (error) {
		console.error('Error detecting ZIP-based format:', error);
		return 'application/zip';
	}
};

/**
 * Get real MIME type based on file signature
 */
const getRealMimeType = async (file) => {
	try {
		const signature = await getFileSignature(file);
		
		if (signature === '504B0304') {
			const format = await detectKnownFormatFromBuffer(file);
			return format;
		}	

		if (signature === 'D0CF11E0') {
			const legacyMime = await detectLegacyOfficeFormat(file);
			return legacyMime;
		}
		
		const fileType = findFileTypeBySignature(signature);
		if (fileType) {
			return fileType.mimeType;
		}
		
		const bytes = await getFileBytes(file, 512);
		if (isAsciiText(bytes)) {
			return 'text/plain';
		}

		return 'unknown';
	} catch (error) {
		console.error('Error getting real MIME type:', error);
		return 'unknown';
	}
};

/**
 * Get expected extension for a MIME type
 */
const getExpectedExtension = (mimeType) => {
	const fileType = findFileTypeByMimeType(mimeType);
	return fileType ? fileType.extension : '';
};

/**
 * Check if file extension has been spoofed
 */
export const checkExtensionSpoofing = async (file) => {
	const realMimeType = await getRealMimeType(file);
	const expectedExtension = getExpectedExtension(realMimeType);
	const nameParts = file.name.split('.');
	const actualExtension = nameParts.length > 1 ? `.${  nameParts.pop().toLowerCase()}` : '';
	const isSpoofed = expectedExtension && actualExtension && actualExtension !== expectedExtension;

	return {
		fileName: file.name,
		actualExtension,
		expectedExtension,
		realMimeType,
		isSpoofed,
		detectedSignature: await getFileSignature(file)
	};
};

const findFileTypeBySignature = (signature) => {
	return fileTypeRegistry.find(entry => entry.signature === signature) || null;
};

const findFileTypeByMimeType = (mimeType) => {
	return fileTypeRegistry.find(entry => entry.mimeType === mimeType) || null;
};
